import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import styles from './VerifyEmailPage.module.css';

export default function VerifyEmailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();

  const emailFromState = location.state?.email || '';
  const [email, setEmail] = useState(emailFromState);
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const refs = useRef([]);

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  const handleDigit = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...digits];
    next[i] = val;
    setDigits(next);
    if (val && i < 5) refs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) refs.current[i - 1]?.focus();
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text.length === 6) {
      setDigits(text.split(''));
      refs.current[5]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const code = digits.join('');
    if (code.length < 6) return setError('Wpisz pełny 6-cyfrowy kod');
    setError(null);
    setLoading(true);
    try {
      const res = await fetch('/api/auth/verify-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      login(data.token, data.user);
      navigate('/moje-ogloszenia');
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    setResending(true);
    setResent(false);
    setError(null);
    try {
      const res = await fetch('/api/auth/resend-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setResent(true);
      setCountdown(60);
      setDigits(['', '', '', '', '', '']);
      refs.current[0]?.focus();
    } catch (err) {
      setError(err.message);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.icon}>✉️</div>
        <h1>Sprawdź e-mail</h1>
        <p className={styles.sub}>
          Wysłaliśmy 6-cyfrowy kod na adres<br />
          <strong>{email || '...'}</strong>
        </p>

        {!emailFromState && (
          <div className={styles.emailField}>
            <input
              type="email"
              placeholder="Twój adres e-mail"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className={styles.emailInput}
            />
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className={styles.digits} onPaste={handlePaste}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={el => refs.current[i] = el}
                className={styles.digit}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={d}
                onChange={e => handleDigit(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                autoFocus={i === 0}
              />
            ))}
          </div>

          {error && <div className={styles.error}>{error}</div>}
          {resent && <div className={styles.success}>Nowy kod został wysłany!</div>}

          <button type="submit" className={styles.submit} disabled={loading}>
            {loading ? 'Weryfikowanie...' : 'Zweryfikuj konto'}
          </button>
        </form>

        <div className={styles.resend}>
          <span>Nie dostałeś kodu? </span>
          <button
            className={styles.resendBtn}
            onClick={handleResend}
            disabled={resending || countdown > 0}
          >
            {resending ? 'Wysyłanie...' : countdown > 0 ? `Wyślij ponownie (${countdown}s)` : 'Wyślij ponownie'}
          </button>
        </div>

        <Link to="/logowanie" className={styles.back}>← Wróć do logowania</Link>
      </div>
    </div>
  );
}
